import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { Observable, BehaviorSubject, combineLatest, map, startWith, catchError, of } from 'rxjs';
import { Router } from '@angular/router';
import { LibSvc } from 'src/app/services/libService/libSvc.service';
import { tabGroupMapping } from 'src/app/services/tabGroupMapping';
import aliasMappingData from 'src/app/services/AliasMapping.json';

@Component({
  selector: 'library-selector',
  templateUrl: './library-selector.component.html',
  styleUrl: './library-selector.component.css',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager
})
export class LibrarySelectorComponent implements OnInit {

  // Define variables
  isLoading: boolean = false;
  libraryNames$!: Observable<string[]>;
  filteredLibraryNames$!: Observable<string[]>;
  searchTerm$ = new BehaviorSubject<string>('');
  errorMessage: string = '';
  selectedLibraryName: string = '';
  private readonly aliasNameMap: Record<string, string> = this.createAliasNameMap();
  private static instanceCount = 0;
  private readonly instanceId = ++LibrarySelectorComponent.instanceCount;

  // Constructor to initialize services and dependencies
  constructor(private libraryService: LibSvc, private router: Router, private cdr: ChangeDetectorRef) {
    console.log(`[LibrarySelector] instance #${this.instanceId} constructed`);
  }

  // Lifecycle hook to initialize the component
  ngOnInit(): void {
    console.log(`[LibrarySelector] instance #${this.instanceId} ngOnInit`);
    this.isLoading = true;
    this.initializeLibraryNames();
    this.initializeFilteredLibraryNames();
  }

  private initializeLibraryNames(): void {
    this.libraryNames$ = this.libraryService.getLibraries().pipe(
      map(libraries => libraries.map(lib => lib.tableName)),
      map(names => {
        console.log(`[LibrarySelector] libraryNames$ emitted (${names.length} names)`);
        return names;
      }),
      catchError(error => {
        console.error('[LibrarySelector] getLibraries() failed', error);
        this.handleError(error);
        return of([]);
      })
    );
  }

  private initializeFilteredLibraryNames(): void {
    console.log('[LibrarySelector] waiting on getLibGroups()...');
    this.libraryService.getLibGroups().subscribe({
      next: userGroups => {
        console.log(`[LibrarySelector] getLibGroups() resolved with ${userGroups.length} groups`);
        this.filteredLibraryNames$ = combineLatest([
          this.libraryNames$,
          this.searchTerm$.pipe(startWith(''))
        ]).pipe(
          map(([libraryNames, searchTerm]) =>
            libraryNames.filter(name =>
              (this.libraryService.isSuperAdmin() || this.libraryService.isAdmin() || this.isLibraryAccessible(name, userGroups)) &&
              name.toLowerCase().includes(searchTerm.toLowerCase())
            )
          )
        );

        this.filteredLibraryNames$.subscribe({
          next: names => {
            console.log(`[LibrarySelector] instance #${this.instanceId} filtered list updated (${names.length} items) - isLoading set to false`);
            this.errorMessage = names.length > 0 ? '' : 'No libraries found with your access level';
            this.isLoading = false;
            // Force a synchronous view refresh in case this resolved outside a
            // change-detection-triggering context (e.g. MSAL/crypto callbacks).
            this.cdr.detectChanges();
          },
          error: error => {
            console.error('[LibrarySelector] error while filtering library names', error);
            this.handleError(error);
            this.cdr.detectChanges();
          }
        });
      },
      error: error => {
        console.error('[LibrarySelector] getLibGroups() failed', error);
        this.handleError(error);
        this.cdr.detectChanges();
      }
    });
  }

  private isLibraryAccessible(name: string, userGroups: string[]): boolean {
    return Object.keys(tabGroupMapping).includes(name) &&
      (userGroups ?? []).some(group =>
        Object.values(tabGroupMapping[name])
          .flatMap(group => Object.values(group).flat())
          .includes(group)
      );
  }

  getAliasName(libraryName: string): string | null {
    return this.aliasNameMap[this.normalizeKey(libraryName)] || null;
  }

  private createAliasNameMap(): Record<string, string> {
    const aliasMap: Record<string, string> = {};

    aliasMappingData.forEach(currentEntry => {
      const [mappedKey, mappedValue] = Object.entries(currentEntry)[0] || [];

      if (mappedKey && mappedValue) {
        aliasMap[this.normalizeKey(mappedKey)] = mappedValue;
      }
    });

    return aliasMap;
  }

  private normalizeKey(value: string): string {
    return value.trim().toLowerCase();
  }

  // Function to navigate to the selected library
  selectLibrary(libraryName: string): void {
    this.selectedLibraryName = libraryName;
    this.router.navigate([`/lib-editor/${libraryName}`]);
  }

  // Event handler for search term change
  onSearchTermChange(event: any): void {
    const input = event as HTMLSelectElement;
    let searchTerm = input.value;
    this.searchTerm$.next(searchTerm);
  }

  private handleError = (error: { status: number; }) => {
    if (error.status === 404) {
      this.errorMessage = 'Failed to load resource: the server responded with a status of 404 (Not Found)';
    } else if (error.status === 401) {
      this.errorMessage = 'Unauthorized access: the server responded with a status of 401 (Unauthorized)';
    } else if (error.status === 500) {
      this.errorMessage = 'Internal server error: the server responded with a status of 500 (Internal Server Error)';
    } else {
      this.errorMessage = 'An unexpected error occurred';
    }
    this.isLoading = false;
  }
}